import React, { useState, useEffect } from "react";
import { Box, Button, TextField, TablePagination, FormControl, MenuItem, OutlinedInput, Select } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import moment from "moment-timezone";

import { endPoints } from "../../../constant/Environment";
import { getAllData, getAllFilterData } from "../../../Utility/API";
import { getSessonId } from "../../../Utility/axiosInstances";
import PageHeader from "../../../components/PageHeader";
import SectionCard from "../../../components/SectionCard";
import Loader from "../../../components/Loader";
import { tokens } from "../../../theme";
import { mono, pill, th, td, filterFieldSx, TablePaginationActions } from "../../../components/tableKit";

const eventKind = (eventType) => {
  const s = String(eventType || "").toUpperCase();
  if (s.includes("LOGIN")) return "ok";
  if (s.includes("ABANDONED") || s.includes("FAILED")) return "bad";
  if (s.includes("DEPOSIT") || s.includes("CHECKOUT")) return "warn";
  return "neutral";
};

// Mirrors the backend EventType enum: the constant is what the filter sends,
// the label is the event name it records.
const EVENT_TYPES = [
  ["VIEW_CONTENT", "ViewContent"],
  ["LOGIN", "CompleteLogin"],
  ["SOCIAL_LOGIN", "CompleteLogin (Social)"],
  ["INITIATE_REGISTRATION", "InitiateRegistration"],
  ["PROCESS_REGISTRATION", "ProcessRegistration"],
  ["COMPLETE_REGISTRATION", "CompleteRegistration"],
  ["FIRST_PLAY", "FirstPlay"],
  ["FREE_SPIN_USED", "FreeSpinUsed"],
  ["STREAK_STARTED", "StreakStarted"],
  ["BOOSTER_USED", "BoosterUsed"],
  ["KYC_START", "KYCStart"],
  ["KYC_PASSED", "KYCPassed"],
  ["KYC_FAIELD", "KYCFailed"],
  ["INITIATE_CHECKOUT", "InitiateCheckout"],
  ["PURCHASE", "Purchase"],
  ["FIRST_DEPOSIT", "FirstDeposit"],
  ["REPEAT_DEPOSIT", "RepeatDeposit"],
  ["VIP_REACHED", "VIPReached"],
  ["DEPOSIT_ABANDONED", "DepositAbandoned"],
  ["WOF_ACTIVATED", "WofActivated"],
  ["STREAK_ACTIVATED", "StreakActivated"],
  ["TELEGRAM_LINKED", "TelegramLinked"],
  ["CHURN_RISK_HIGH", "ChurnRiskHigh"],
  ["CASHIER_OPENED", "CashierOpened"],
  ["PAYMENT_ATTEMPTED", "PaymentAttempted"],
  ["PAYMENT_FAILED", "PaymentFailed"],
  ["WALLET_CREDITED", "WalletCredited"],
];

const fmtDateTime = (d) =>
  d && moment.utc(d).isValid() ? moment.utc(d).format("YYYY-MM-DD HH:mm:ss") : "";

const csvBtnSx = { textTransform: "none", fontSize: 12, color: tokens.textDim, borderColor: tokens.border, "&:hover": { borderColor: tokens.border2 } };

const ActionList = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [playerList, setPlayerList] = useState([]);
  const [eventTypeFilter, setEventTypeFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [playerTypeFilter, setPlayerTypeFilter] = useState("");

  useEffect(() => {
    fetchUserData();
    getPlayerTypes();
  }, []);

  const getPlayerTypes = async () => {
    try {
      const response = await getAllData(`${endPoints.api.playertypes}`);
      if (response?.status === "success") {
        setPlayerList(response.data || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // `overrides` lets Clear search with explicit values instead of waiting
  // on state updates to land. playerType 0 means all player types.
  const fetchUserData = async (overrides = {}) => {
    const filters = { eventType: eventTypeFilter, fromDate, toDate, playerType: playerTypeFilter, ...overrides };
    const body = {
      eventType: filters.eventType,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      playerType: Number(filters.playerType) || 0,
    };
    setIsLoading(true);
    setPage(0);
    await getAllFilterData(`${endPoints.api.GET_Action_List}`, body)
      .then((response) => {
        setIsLoading(false);
        if (response?.status === "success") {
          setRows(response.data || []);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error(error);
      });
  };

  const clearFilters = () => {
    setEventTypeFilter("");
    setFromDate("");
    setToDate("");
    setPlayerTypeFilter("");
    setFilterText("");
    fetchUserData({ eventType: "", fromDate: "", toDate: "", playerType: "" });
  };

  const exportReport = (format) => {
    const body = { userId: 0, startdate: "", enddate: "" };
    // Raw fetch, so it misses the axios interceptor that attaches SessonId —
    // set it by hand or the API answers 401 and we save the error body as the
    // report. Accept admits the octet-stream the download endpoints produce.
    const sessonId = getSessonId();
    fetch(`${endPoints.apiBaseUrl + endPoints.api.ACTION_REPORT + "/" + format}`, {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
        ...(sessonId ? { SessonId: sessonId } : {}),
      },
    }).then(function (response) {
      response.blob().then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `ActionListReport.${format}`;
        link.click();
      });
    });
  };

  const filtered = filterText
    ? rows.filter((item) => {
        const q = filterText.toLowerCase();
        return (
          String(item.login || "").toLowerCase().includes(q) ||
          String(item.eventType || "").toLowerCase().includes(q) ||
          String(item.eventName || "").toLowerCase().includes(q) ||
          String(item.idUser || "").includes(q)
        );
      })
    : rows;

  return (
    <Box>
      {isLoading && <Loader spinner={true} visible={isLoading} />}

      <PageHeader
        title="Action List"
        crumb="Users / Action List"
        subtitle="Player events · logins, registrations, deposits"
      />

      <SectionCard>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", mb: 2 }}>
          <TextField
            size="small"
            placeholder="Filter by login, event or user ID"
            value={filterText}
            onChange={(e) => {
              setFilterText(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 260, "& .MuiInputBase-root": { fontSize: 13 } }}
          />

          <FormControl size="small" sx={filterFieldSx}>
            <Select
              displayEmpty
              value={eventTypeFilter}
              onChange={(e) => setEventTypeFilter(e.target.value)}
              input={<OutlinedInput />}
              sx={{ fontSize: 13 }}
              renderValue={(selected) =>
                selected === "" ? (
                  <span style={{ color: tokens.textDim }}>All event types</span>
                ) : (
                  EVENT_TYPES.find(([value]) => value === selected)?.[1] || selected
                )
              }
              MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
            >
              <MenuItem value="">All event types</MenuItem>
              {EVENT_TYPES.map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={filterFieldSx}>
            <Select
              displayEmpty
              value={playerTypeFilter}
              onChange={(e) => setPlayerTypeFilter(e.target.value)}
              input={<OutlinedInput />}
              sx={{ fontSize: 13 }}
              renderValue={(selected) =>
                selected === "" ? (
                  <span style={{ color: tokens.textDim }}>All player types</span>
                ) : (
                  playerList.find((p) => p.id === Number(selected))?.player_type || selected
                )
              }
              MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
            >
              <MenuItem value="">All player types</MenuItem>
              {playerList.map((player) => (
                <MenuItem key={player.id} value={player.id}>
                  {player.player_type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            type="date"
            label="From"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ ...filterFieldSx, minWidth: 150 }}
          />

          <TextField
            size="small"
            type="date"
            label="To"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ ...filterFieldSx, minWidth: 150 }}
          />

          <Button variant="contained" size="small" onClick={() => fetchUserData()} sx={{ textTransform: "none" }}>
            Search
          </Button>
          <Button variant="outlined" size="small" color="inherit" onClick={clearFilters} sx={{ textTransform: "none", color: tokens.textDim, borderColor: tokens.border }}>
            Clear
          </Button>
          <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
            <Button size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon />} sx={csvBtnSx} onClick={() => exportReport("pdf")}>
              PDF
            </Button>
            <Button size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon />} sx={csvBtnSx} onClick={() => exportReport("csv")}>
              CSV
            </Button>
          </Box>
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Event Type</th>
                <th style={th}>Event Name</th>
                <th style={th}>Insert At</th>
                <th style={th}>User ID</th>
                <th style={th}>Fbclid</th>
                <th style={th}>Login</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={6} style={{ ...td, textAlign: "center", color: tokens.textDim }}>
                    No events found
                  </td>
                </tr>
              )}
              {filtered
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, i) => (
                  <tr key={`${row.idUser}-${row.insertionTime}-${i}`}>
                    <td style={td}>{row.eventType ? pill(row.eventType, eventKind(row.eventType)) : ""}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{row.eventName}</td>
                    <td style={{ ...td, fontFamily: mono, color: tokens.textDim }}>{fmtDateTime(row.insertionTime)}</td>
                    <td style={{ ...td, fontFamily: mono }}>{row.idUser}</td>
                    <td style={{ ...td, fontFamily: mono, color: tokens.textDim2, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {row.fbclid || "—"}
                    </td>
                    <td style={{ ...td, fontWeight: 600 }}>{row.login}</td>
                  </tr>
                ))}
            </tbody>
          </Box>
        </Box>

        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 25, 50, 100]}
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          SelectProps={{ inputProps: { "aria-label": "rows per page" }, native: true }}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          ActionsComponent={TablePaginationActions}
          sx={{ borderTop: `1px solid ${tokens.border}`, mt: 1 }}
        />
      </SectionCard>
    </Box>
  );
};

export default ActionList;
